import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { DashboardLayout } from '../components/Layout';
import api from '../services/api';
import ReportGridPro from '../components/analytics/ReportGridPro';
import ReportChartsModal from '../components/analytics/ReportChartsModal';

const fmtDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('pt-PT');
  } catch {
    return '';
  }
};

const fmtDateTime = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString('pt-PT');
  } catch {
    return '';
  }
};

const fmtMoney = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' });
};

const monthKey = (dateStr) => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const aggregateSum = (rows, keyFn, valueKey, limit = null) => {
  const map = new Map();
  (rows || []).forEach(r => {
    const k = keyFn(r);
    const v = Number(r?.[valueKey] || 0);
    map.set(k, (map.get(k) || 0) + v);
  });
  const out = Array.from(map.entries()).map(([k, v]) => ({ key: k, value: v }));
  out.sort((a, b) => b.value - a.value);
  const trimmed = typeof limit === 'number' ? out.slice(0, limit) : out;
  return trimmed.map(x => ({ label: x.key, value: Math.round(x.value * 100) / 100 }));
};

const tinyBtn = {
  border: '1px solid var(--border-color)',
  background: 'var(--bg-card)',
  padding: '8px 10px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 800,
};

export default function AdminAnalyticsProPage() {
  const [reportId, setReportId] = useState('sales');
  const [start, setStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [chartsOpen, setChartsOpen] = useState(false);

  // Conta Corrente (ledger) parameters
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  const reportDefs = useMemo(() => {
    return [
      {
        id: 'sales',
        label: 'Relatório de Vendas',
        endpoint: '/analytics/reports/sales',
        defaultColumnVisibility: {
          // Visible by default (ERP-like list)
          documentType: true,
          number: true,
          series: true,
          issueDate: true,
          dueDate: true,
          clientName: true,
          clientNif: true,
          status: true,
          totalNet: true,
          taxAmount: true,
          total: true,
          paidAmount: true,
          pendingAmount: true,
          overdueAmount: true,
          paymentMethod: true,
          vendorName: true,

          // Hidden by default (available in “Colunas”)
          clientId: false,
          clientEmail: false,
          createdAt: false,
          subtotal: false,
          lineDiscount: false,
          globalDiscountAmount: false,
          totalDiscount: false,
          shippingCost: false,
          amountDue: false,
          daysOverdue: false,
          paymentTerms: false,
          externalReference: false,
          currency: false,
        },
        columns: [
          { accessorKey: 'documentType', header: 'Doc', enableGrouping: true },
          { accessorKey: 'number', header: 'Nº Doc', enableGrouping: true },
          { accessorKey: 'series', header: 'Série', enableGrouping: true },
          { accessorKey: 'issueDate', header: 'Data', enableGrouping: true, cell: (info) => fmtDate(info.getValue()) },
          { accessorKey: 'dueDate', header: 'Data Vto.', enableGrouping: true, cell: (info) => fmtDate(info.getValue()) },
          { accessorKey: 'clientName', header: 'Entidade', enableGrouping: true },
          { accessorKey: 'clientNif', header: 'NIF', enableGrouping: true },
          { accessorKey: 'clientId', header: 'Cod. Enti.', enableGrouping: true },
          { accessorKey: 'clientEmail', header: 'Email', enableGrouping: true },
          { accessorKey: 'status', header: 'Estado', enableGrouping: true },
          { accessorKey: 'createdAt', header: 'Data Grava', enableGrouping: true, cell: (info) => fmtDateTime(info.getValue()) },

          { accessorKey: 'subtotal', header: 'Total Bruto', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'lineDiscount', header: 'Desc. Linhas', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'globalDiscountAmount', header: 'Desc. Global', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          {
            id: 'totalDiscount',
            header: 'Total Desconto',
            accessorFn: (row) => Number(row?.lineDiscount || 0) + Number(row?.globalDiscountAmount || 0),
            aggregationFn: 'sum',
            cell: (info) => fmtMoney(info.getValue()),
            aggregatedCell: (info) => fmtMoney(info.getValue()),
          },
          { accessorKey: 'shippingCost', header: 'Portes', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },

          { accessorKey: 'totalNet', header: 'Total Líquido', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'taxAmount', header: 'Total Impostos', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'total', header: 'Total Documento', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'paidAmount', header: 'Valor Pago', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'pendingAmount', header: 'Valor Pendente', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          {
            id: 'overdueAmount',
            header: 'Valor Vencido',
            accessorFn: (row) => {
              const pending = Number(row?.pendingAmount || 0);
              if (pending <= 0) return 0;
              const due = row?.dueDate ? new Date(row.dueDate) : null;
              if (!due || Number.isNaN(due.getTime())) return 0;
              return due < new Date() ? pending : 0;
            },
            aggregationFn: 'sum',
            cell: (info) => fmtMoney(info.getValue()),
            aggregatedCell: (info) => fmtMoney(info.getValue()),
          },
          {
            id: 'daysOverdue',
            header: 'Dias Atraso',
            accessorFn: (row) => {
              const pending = Number(row?.pendingAmount || 0);
              if (pending <= 0) return 0;
              const due = row?.dueDate ? new Date(row.dueDate) : null;
              if (!due || Number.isNaN(due.getTime())) return 0;
              const diff = Math.floor((new Date().getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
              return diff > 0 ? diff : 0;
            },
            cell: (info) => String(info.getValue() || 0),
          },
          { accessorKey: 'amountDue', header: 'Valor Vencer', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'paymentMethod', header: 'Pagamento', enableGrouping: true },
          { accessorKey: 'vendorName', header: 'Vendedor', enableGrouping: true },

          { accessorKey: 'paymentTerms', header: 'Condições', enableGrouping: true },
          { accessorKey: 'externalReference', header: 'V/ Ref.', enableGrouping: true },
          { accessorKey: 'currency', header: 'Moeda', enableGrouping: true },
        ],
      },
      {
        id: 'purchases',
        label: 'Relatório de Compras',
        endpoint: '/analytics/reports/purchases',
        defaultColumnVisibility: {
          invoiceDate: true,
          invoiceNumber: true,
          supplierName: true,
          supplierNif: true,
          status: true,
          subTotal: true,
          taxTotal: true,
          discountTotal: true,
          total: true,
          paymentMethod: true,
          supplierReference: true,
          dueDate: true,
          paidDate: false,
          notes: false,
          createdAt: false,
          paymentTerms: false,
        },
        columns: [
          { accessorKey: 'invoiceDate', header: 'Data', enableGrouping: true, cell: (info) => fmtDate(info.getValue()) },
          { accessorKey: 'invoiceNumber', header: 'Nº Fatura', enableGrouping: true },
          { accessorKey: 'supplierName', header: 'Fornecedor', enableGrouping: true },
          { accessorKey: 'supplierNif', header: 'NIF', enableGrouping: true },
          { accessorKey: 'status', header: 'Estado', enableGrouping: true },
          { accessorKey: 'dueDate', header: 'Data Vto.', enableGrouping: true, cell: (info) => fmtDate(info.getValue()) },
          { accessorKey: 'paidDate', header: 'Data Pag.', enableGrouping: true, cell: (info) => fmtDate(info.getValue()) },
          { accessorKey: 'subTotal', header: 'Subtotal', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'taxTotal', header: 'Impostos', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'discountTotal', header: 'Descontos', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'total', header: 'Total', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'paymentMethod', header: 'Pagamento', enableGrouping: true },
          { accessorKey: 'supplierReference', header: 'Ref. Fornecedor', enableGrouping: true },
          { accessorKey: 'paymentTerms', header: 'Condições', enableGrouping: true },
          { accessorKey: 'notes', header: 'Obs.', enableGrouping: true },
          { accessorKey: 'createdAt', header: 'Data Grava', enableGrouping: true, cell: (info) => fmtDateTime(info.getValue()) },
        ],
      },
      {
        id: 'salesByArticle',
        label: 'Vendas por Artigo',
        endpoint: '/analytics/reports/sales-by-article',
        defaultColumnVisibility: {
          reference: true,
          description: true,
          qty: true,
          avgUnitPrice: true,
          total: true,
          lines: true,
          brand: false,
          family: false,
          category: false,
          articleType: false,
        },
        columns: [
          { accessorKey: 'reference', header: 'Referência', enableGrouping: true },
          { accessorKey: 'description', header: 'Descrição', enableGrouping: true },
          { accessorKey: 'brand', header: 'Marca', enableGrouping: true },
          { accessorKey: 'family', header: 'Família', enableGrouping: true },
          { accessorKey: 'category', header: 'Categoria', enableGrouping: true },
          { accessorKey: 'articleType', header: 'Tipo', enableGrouping: true },
          { accessorKey: 'qty', header: 'Qtd', aggregationFn: 'sum' },
          { accessorKey: 'avgUnitPrice', header: 'Preço Médio', cell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'total', header: 'Total Vendas', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'lines', header: 'Linhas', aggregationFn: 'sum' },
        ],
      },
      {
        id: 'salesByClient',
        label: 'Vendas por Cliente',
        endpoint: '/analytics/reports/sales-by-client',
        defaultColumnVisibility: {
          clientName: true,
          clientNif: true,
          invoiceCount: true,
          total: true,
          paidAmount: true,
          pendingAmount: true,
          totalNet: false,
          taxAmount: false,
          clientEmail: false,
          lastIssueDate: true,
        },
        columns: [
          { accessorKey: 'clientName', header: 'Cliente', enableGrouping: true },
          { accessorKey: 'clientNif', header: 'NIF', enableGrouping: true },
          { accessorKey: 'clientEmail', header: 'Email', enableGrouping: true },
          { accessorKey: 'invoiceCount', header: 'Docs', aggregationFn: 'sum' },
          { accessorKey: 'totalNet', header: 'Líquido', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'taxAmount', header: 'Impostos', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'total', header: 'Total', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'paidAmount', header: 'Pago', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'pendingAmount', header: 'Pendente', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'lastIssueDate', header: 'Última', cell: (info) => fmtDate(info.getValue()) },
        ],
      },
      {
        id: 'receipts',
        label: 'Relatório de Recibos',
        endpoint: '/analytics/reports/receipts',
        defaultColumnVisibility: {
          paymentDate: true,
          receiptNumber: true,
          series: true,
          clientName: true,
          clientNif: true,
          paymentMethod: true,
          totalReceived: true,
          amount: false,
          retentionAmount: false,
          discountAmount: false,
          otherAmount: false,
          transactionReference: false,
          createdAt: false,
          notes: false,
        },
        columns: [
          { accessorKey: 'paymentDate', header: 'Data', enableGrouping: true, cell: (info) => fmtDate(info.getValue()) },
          { accessorKey: 'receiptNumber', header: 'Nº Recibo', enableGrouping: true },
          { accessorKey: 'series', header: 'Série', enableGrouping: true },
          { accessorKey: 'clientName', header: 'Cliente', enableGrouping: true },
          { accessorKey: 'clientNif', header: 'NIF', enableGrouping: true },
          { accessorKey: 'paymentMethod', header: 'Pagamento', enableGrouping: true },
          { accessorKey: 'amount', header: 'Valor', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'retentionAmount', header: 'Retenção', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'discountAmount', header: 'Desconto', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'otherAmount', header: 'Outros', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'totalReceived', header: 'Total Recebido', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'transactionReference', header: 'Ref. Transação', enableGrouping: true },
          { accessorKey: 'createdAt', header: 'Data Grava', enableGrouping: true, cell: (info) => fmtDateTime(info.getValue()) },
          { accessorKey: 'notes', header: 'Obs.', enableGrouping: true },
        ],
      },
      {
        id: 'accountLedger',
        label: 'Conta Corrente (Extrato)',
        endpoint: '/analytics/reports/account-ledger',
        defaultColumnVisibility: {
          date: true,
          type: true,
          number: true,
          debit: true,
          credit: true,
          balance: true,
          status: true,
          id: false,
        },
        columns: [
          { accessorKey: 'date', header: 'Data', enableGrouping: true, cell: (info) => fmtDate(info.getValue()) },
          { accessorKey: 'type', header: 'Tipo', enableGrouping: true },
          { accessorKey: 'number', header: 'Documento', enableGrouping: true },
          { accessorKey: 'debit', header: 'Débito', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'credit', header: 'Crédito', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'balance', header: 'Saldo', cell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'status', header: 'Estado', enableGrouping: true },
          { accessorKey: 'id', header: 'ID', enableGrouping: true },
        ],
      },
      {
        id: 'profit',
        label: 'Mapa de Lucros (Margem Bruta)',
        endpoint: '/analytics/reports/profit',
        defaultColumnVisibility: {
          issueDate: true,
          invoiceNumber: true,
          documentType: false,
          status: false,
          clientName: true,
          reference: true,
          description: true,
          brand: false,
          family: false,
          qty: true,
          saleTotal: true,
          costTotal: true,
          profit: true,
          marginPercent: true,
          costUnit: false,
        },
        columns: [
          { accessorKey: 'issueDate', header: 'Data', enableGrouping: true, cell: (info) => fmtDate(info.getValue()) },
          { accessorKey: 'invoiceNumber', header: 'Documento', enableGrouping: true },
          { accessorKey: 'documentType', header: 'Doc', enableGrouping: true },
          { accessorKey: 'status', header: 'Estado', enableGrouping: true },
          { accessorKey: 'clientName', header: 'Cliente', enableGrouping: true },
          { accessorKey: 'reference', header: 'Referência', enableGrouping: true },
          { accessorKey: 'description', header: 'Descrição', enableGrouping: true },
          { accessorKey: 'brand', header: 'Marca', enableGrouping: true },
          { accessorKey: 'family', header: 'Família', enableGrouping: true },
          { accessorKey: 'qty', header: 'Qtd', aggregationFn: 'sum' },
          { accessorKey: 'saleTotal', header: 'Venda', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'costUnit', header: 'Custo Unit.', cell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'costTotal', header: 'Custo', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'profit', header: 'Lucro', aggregationFn: 'sum', cell: (info) => fmtMoney(info.getValue()), aggregatedCell: (info) => fmtMoney(info.getValue()) },
          { accessorKey: 'marginPercent', header: 'Margem %', cell: (info) => `${Number(info.getValue() || 0).toFixed(2)}%` },
        ],
      },
    ];
  }, []);

  const activeReport = useMemo(() => reportDefs.find(r => r.id === reportId) || reportDefs[0], [reportDefs, reportId]);

  const searchClients = async (q) => {
    setClientSearch(q);
    if ((q || '').trim().length < 2) {
      setClientResults([]);
      return;
    }
    try {
      const res = await api.get(`/billing/users/search?q=${encodeURIComponent(q)}`);
      setClientResults(Array.isArray(res.data) ? res.data : []);
    } catch {
      setClientResults([]);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        start: start ? new Date(start).toISOString() : null,
        end: end ? new Date(end).toISOString() : null,
        includeCancelled,
      };

      if (reportId === 'accountLedger') {
        if (!selectedClient?.id) {
          setRows([]);
          toast.info('Selecione um cliente para ver a Conta Corrente.');
          return;
        }
        params.userId = selectedClient.id;
      }

      const res = await api.get(activeReport.endpoint, { params });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setRows([]);
      toast.error(e?.response?.data?.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const chartPresets = useMemo(() => {
    const r = rows || [];
    if (reportId === 'sales') {
      const byMonth = aggregateSum(r, x => monthKey(x.issueDate), 'total');
      const byClient = aggregateSum(r, x => x.clientName || '—', 'total', 12);
      const byDoc = aggregateSum(r, x => x.documentType || '—', 'total');
      return [
        { id: 'sales-month', label: 'Vendas por Mês (Total)', type: 'line', data: byMonth.map(x => ({ x: x.label, y: x.value })), xKey: 'x', yKey: 'y', yLabel: 'Total' },
        { id: 'sales-client', label: 'Top Clientes (Total)', type: 'bar', data: byClient.map(x => ({ x: x.label, y: x.value })), xKey: 'x', yKey: 'y', yLabel: 'Total' },
        { id: 'sales-doc', label: 'Total por Tipo Doc', type: 'pie', data: byDoc.map(x => ({ x: x.label, y: x.value })), xKey: 'x', yKey: 'y', yLabel: 'Total' },
      ];
    }
    if (reportId === 'purchases') {
      const byMonth = aggregateSum(r, x => monthKey(x.invoiceDate), 'total');
      const bySupplier = aggregateSum(r, x => x.supplierName || '—', 'total', 12);
      return [
        { id: 'purch-month', label: 'Compras por Mês (Total)', type: 'line', data: byMonth.map(x => ({ x: x.label, y: x.value })), xKey: 'x', yKey: 'y', yLabel: 'Total' },
        { id: 'purch-supplier', label: 'Top Fornecedores (Total)', type: 'bar', data: bySupplier.map(x => ({ x: x.label, y: x.value })), xKey: 'x', yKey: 'y', yLabel: 'Total' },
      ];
    }
    if (reportId === 'salesByArticle') {
      const top = (r || []).slice(0, 15).map(x => ({ x: x.reference || x.description || '—', y: Number(x.total || 0) }));
      return [
        { id: 'sba-top', label: 'Top Artigos (Total)', type: 'bar', data: top, xKey: 'x', yKey: 'y', yLabel: 'Total' },
      ];
    }
    if (reportId === 'salesByClient') {
      const top = (r || []).slice(0, 15).map(x => ({ x: x.clientName || '—', y: Number(x.total || 0) }));
      return [
        { id: 'sbc-top', label: 'Top Clientes (Total)', type: 'bar', data: top, xKey: 'x', yKey: 'y', yLabel: 'Total' },
      ];
    }
    if (reportId === 'receipts') {
      const byMethod = aggregateSum(r, x => x.paymentMethod || '—', 'totalReceived');
      const byMonth = aggregateSum(r, x => monthKey(x.paymentDate), 'totalReceived');
      return [
        { id: 'rcpt-month', label: 'Recebimentos por Mês', type: 'line', data: byMonth.map(x => ({ x: x.label, y: x.value })), xKey: 'x', yKey: 'y', yLabel: 'Total Recebido' },
        { id: 'rcpt-method', label: 'Recebimentos por Método', type: 'pie', data: byMethod.map(x => ({ x: x.label, y: x.value })), xKey: 'x', yKey: 'y', yLabel: 'Total Recebido' },
      ];
    }
    if (reportId === 'accountLedger') {
      const byType = aggregateSum(r, x => x.type || '—', 'debit');
      return [
        { id: 'cc-type', label: 'Débitos por Tipo', type: 'pie', data: byType.map(x => ({ x: x.label, y: x.value })), xKey: 'x', yKey: 'y', yLabel: 'Débito' },
      ];
    }
    if (reportId === 'profit') {
      const byMonth = aggregateSum(r, x => monthKey(x.issueDate), 'profit');
      const byFamily = aggregateSum(r, x => x.family || '—', 'profit', 12);
      return [
        { id: 'profit-month', label: 'Lucro por Mês', type: 'line', data: byMonth.map(x => ({ x: x.label, y: x.value })), xKey: 'x', yKey: 'y', yLabel: 'Lucro' },
        { id: 'profit-family', label: 'Top Famílias (Lucro)', type: 'bar', data: byFamily.map(x => ({ x: x.label, y: x.value })), xKey: 'x', yKey: 'y', yLabel: 'Lucro' },
      ];
    }
    return [];
  }, [rows, reportId]);

  return (
    <DashboardLayout userType="admin">
      <div style={{ padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 20 }}>📈 Analytics — Relatórios PRO</h1>
            <div style={{ color: 'var(--info)', fontSize: 13, marginTop: 4 }}>
              Relatórios com agrupamento por arrastar colunas, filtros avançados e gráficos em popup.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={reportId} onChange={(e) => setReportId(e.target.value)} style={{ ...tinyBtn, padding: '8px 12px' }}>
              {reportDefs.map(r => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>

            {reportId === 'accountLedger' && (
              <div style={{ position: 'relative', minWidth: 320 }}>
                <input
                  value={clientSearch}
                  onChange={(e) => {
                    setSelectedClient(null);
                    searchClients(e.target.value);
                  }}
                  placeholder="👤 Cliente (pesquisar...)"
                  style={{
                    padding: '8px 10px',
                    borderRadius: 10,
                    border: '1px solid var(--border-color)',
                    width: '100%',
                    outline: 'none',
                  }}
                />
                {clientResults.length > 0 && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 10, zIndex: 20, maxHeight: 260, overflow: 'auto', boxShadow: '0 12px 30px rgba(0,0,0,.15)' }}>
                    {clientResults.slice(0, 25).map(u => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => {
                          setSelectedClient(u);
                          setClientSearch(u.name);
                          setClientResults([]);
                          setRows([]);
                        }}
                        style={{ width: '100%', textAlign: 'left', padding: '10px 10px', border: 'none', background: 'var(--bg-card)', cursor: 'pointer' }}
                      >
                        <div style={{ fontWeight: 800, color: 'var(--info-text)' }}>{u.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--info)' }}>{u.nif ? `NIF: ${u.nif}` : '—'} {u.email ? `• ${u.email}` : ''}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--info-text)', fontWeight: 800 }}>
              Início
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={{ ...tinyBtn, padding: '6px 8px', fontWeight: 700 }} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--info-text)', fontWeight: 800 }}>
              Fim
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={{ ...tinyBtn, padding: '6px 8px', fontWeight: 700 }} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--info-text)' }}>
              <input type="checkbox" checked={includeCancelled} onChange={(e) => setIncludeCancelled(e.target.checked)} />
              Incluir anulados
            </label>
            <button type="button" onClick={load} style={{ ...tinyBtn, background: 'var(--info)', borderColor: 'var(--info)', color: '#fff' }}>
              ⚡ Executar
            </button>
          </div>
        </div>

        {/* Chart buttons ribbon (like the example screenshot) */}
        <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" style={{ ...tinyBtn, display: 'flex', alignItems: 'center', gap: 8 }} onClick={() => setChartsOpen(true)}>
            📊 Gráficos
          </button>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" style={tinyBtn} onClick={() => { setReportId('sales'); }}>📈 Vendas</button>
            <button type="button" style={tinyBtn} onClick={() => { setReportId('purchases'); }}>📦 Compras</button>
            <button type="button" style={tinyBtn} onClick={() => { setReportId('salesByArticle'); }}>🧾 Vendas/Artigo</button>
            <button type="button" style={tinyBtn} onClick={() => { setReportId('salesByClient'); }}>👤 Vendas/Cliente</button>
            <button type="button" style={tinyBtn} onClick={() => { setReportId('receipts'); }}>🧾 Recibos</button>
            <button type="button" style={tinyBtn} onClick={() => { setReportId('accountLedger'); }}>📒 Conta Corrente</button>
            <button type="button" style={tinyBtn} onClick={() => { setReportId('profit'); }}>💹 Lucros</button>
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <ReportGridPro
            title={activeReport.label}
            columns={activeReport.columns}
            data={rows}
            loading={loading}
            defaultGrouping={[]}
            initialSort={[]}
            defaultColumnVisibility={activeReport.defaultColumnVisibility || {}}
            storageKey={
              reportId === 'accountLedger'
                ? `analytics-pro:${reportId}:client:${selectedClient?.id || 'none'}`
                : `analytics-pro:${reportId}`
            }
          />
        </div>

        <ReportChartsModal
          open={chartsOpen}
          onClose={() => setChartsOpen(false)}
          title={activeReport.label}
          presets={chartPresets}
        />
      </div>
    </DashboardLayout>
  );
}
